import { eq } from "@-/com.npmjs.drizzle-orm";
import type { ActorDatabaseHandle } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Schedule first pronunciation reviews when enabling them, without changing learned cards. */
export async function schedulePronunciationBacklog(
  tx: Parameters<Parameters<ActorDatabaseHandle["transaction"]>[0]>[0],
  now: Date,
) {
  const meaningCards = await tx.query.userGraphemeMeaningCard.findMany({
    with: { userCard: { with: { fsrsCard: true } } },
  });
  const meaningDueByGrapheme = new Map<string, number>();
  for (const meaning of meaningCards) {
    const card = meaning.userCard;
    if (card?.startedStudyingAt != null && card.fsrsCard != null) {
      meaningDueByGrapheme.set(meaning.grapheme, new Date(card.fsrsCard.due).getTime());
    }
  }

  const pronunciationCards = await tx.query.userGraphemePronunciationCard.findMany({
    with: { userCard: { with: { fsrsCard: true } } },
  });
  const pending: { due: number; id: string }[] = [];
  const nowMs = now.getTime();
  const distant: { due: number; id: string }[] = [];
  for (const pronunciation of pronunciationCards) {
    const card = pronunciation.userCard;
    const fsrs = card?.fsrsCard;
    const meaningDue = meaningDueByGrapheme.get(pronunciation.grapheme);
    if (
      fsrs == null ||
      meaningDue === undefined ||
      card?.startedStudyingAt == null ||
      card.manuallyMasteredAt != null ||
      fsrs.lastReview !== null ||
      fsrs.reps !== 0
    ) {
      continue;
    }
    const scheduled = { due: Math.max(nowMs, meaningDue), id: fsrs.id };
    if (meaningDue > nowMs + WEEK_MS) {
      distant.push(scheduled);
    } else {
      pending.push(scheduled);
    }
  }

  distant.sort((a, b) => a.due - b.due || a.id.localeCompare(b.id));
  for (const [index, card] of distant.entries()) {
    pending.push({
      due: nowMs + Math.round((index * WEEK_MS) / Math.max(1, distant.length - 1)),
      id: card.id,
    });
  }
  for (const card of pending) {
    // This is an introduction date, not a completed review or an FSRS interval.
    await tx
      .update(actorSchema.fsrsCard)
      .set({ due: new Date(card.due).toISOString() })
      .where(eq(actorSchema.fsrsCard.id, card.id));
  }
}
